// src/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from './firebase';
import BootstrapShell from './components/shared/BootstrapShell';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getCanonicalOrgId } from './utils/drillTemplates';
import { loadUserOrganizationData, resolveOrgDisplayLabel } from './utils/organizationData';
import {
  canEnterOrganizationWorkspace,
  canEnterInstructorWorkspace,
  canEnterPersonalWorkspace,
  ORG_OPERATE_ROLES,
} from './utils/platformAccess';
import { getPreferredOrganizationId, setPreferredOrganizationId } from './utils/organizationSelection';

const AuthContext = createContext();
const VIEW_MODE_STORAGE_KEY = 'sas-view-mode';

// Pass 58F — the ACTIVE/PRIMARY organization derives from the CANONICAL membership, not the stale
// legacy users/{uid}.orgId (which can be empty/wrong for a linked owner like John). Prefer an org
// where the user holds an operating role (owner/admin/ops) so the org workspace lands on the right
// org (/org/sas-niseko, not /org/default); otherwise fall back to the first canonical membership.
function resolvePrimaryOrganizationId(memberships = []) {
  if (!Array.isArray(memberships) || memberships.length === 0) return '';
  const operable = memberships.find((m) => ORG_OPERATE_ROLES.includes(m.role));
  return (operable || memberships[0]).organizationId || '';
}

// Pass 86P — ONE canonical selected-organization model. The ACTIVE org is the user's explicitly PREFERRED org
// (the shared `platform-preferred-organization-id` key) WHEN it is one of their real memberships; otherwise the
// deterministic default above. Selecting an org NEVER grants access — it only chooses among already-authorized
// memberships — so AuthContext and the OrganizationSwitcher can never pick conflicting orgs (§6B).
function resolveActiveOrganizationId(memberships = []) {
  if (!Array.isArray(memberships) || memberships.length === 0) return '';
  const preferred = getPreferredOrganizationId();
  if (preferred && memberships.some((m) => m.organizationId === preferred)) return preferred;
  return resolvePrimaryOrganizationId(memberships);
}

// Pass 58C — allowed workspaces derive from CANONICAL entitlement, not the legacy top-level userData.role.
// Organisation workspace = operational permission (owner/admin/ops); Instructor workspace = a teaching
// relationship (isTeachingInstructor), independent of permission. Student view is always available (a
// user's own student surface). This is what gives Sam/Dom/Marc BOTH workspaces and Serena Operations only.
function computeAllowedViewModes(userData = {}, orgId = '') {
  const modes = [];
  if (canEnterOrganizationWorkspace(userData, orgId)) modes.push('organization');
  if (canEnterInstructorWorkspace(userData, orgId)) modes.push('instructor');
  // Pass 58G — the student/personal ("My Coaching") mode requires a genuine personal entitlement, NOT
  // merely being authenticated. An org-only owner/ops (John/Serena) no longer sees My Coaching.
  if (canEnterPersonalWorkspace(userData, orgId)) modes.push('student');
  return modes;
}

function resolveViewMode(userData, orgId, preferredMode) {
  const allowedModes = computeAllowedViewModes(userData, orgId);
  if (preferredMode && allowedModes.includes(preferredMode)) return preferredMode;
  return allowedModes[0];
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(null); // resolved after user data loads

  const refreshUserData = async () => {
    if (auth.currentUser) {
      try {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          const canonicalUserData = {
            ...data,
            orgId: getCanonicalOrgId(data),
          };
          // Pass 58B — the React permission model (canManageOrganization/canOperateOrganization) must see
          // the CANONICAL organizationMembers relationship, not only stale users/{uid}.orgId/orgRole. Resolve
          // memberships by BOTH authUserId and legacy userId (see loadOrganizationMembershipsForUser) and
          // attach them so a seeded instructor who logs in with their Firebase UID is authorized correctly.
          try {
            const { memberships, organizationsById } = await loadUserOrganizationData(db, { currentUser: auth.currentUser, userData: data });
            if (Array.isArray(memberships) && memberships.length) {
              // Pass 87 §4/§18 — attach the HUMAN organisation name so the org switcher and other membership
              // displays never show the raw id (e.g. "motus-pilot-2"). Pass 88.1 — the CANONICAL org metadata WINS
              // over the denormalised membership label: a stale `membership.organizationName` ("Motus Pilot 2") must
              // NOT mask the authoritative `organizations/{id}.displayName` ("Voletus Pilot"). Precedence: canonical
              // displayName → canonical name → cached membership name → id (honest fallback — never fabricated). The
              // organizationId stays the stable identifier underneath (unchanged).
              const orgs = organizationsById || {};
              canonicalUserData.organizationMemberships = memberships.map((m) => ({
                ...m,
                organizationName: resolveOrgDisplayLabel(orgs[m.organizationId], m),
              }));
              // Canonical membership takes precedence: derive the active org from it so a linked owner
              // whose legacy users.orgId is empty (John) still resolves to sas-niseko, not 'default'. Pass 86P —
              // honour the explicit preferred-org selection when it is a valid membership (one canonical model).
              const primaryOrgId = resolveActiveOrganizationId(memberships);
              if (primaryOrgId) canonicalUserData.orgId = primaryOrgId;
            }
          } catch (e) {
            console.warn('Could not resolve canonical organization memberships:', e);
          }
          setUserData(canonicalUserData);
          const savedViewMode = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
          const orgId = canonicalUserData.orgId || canonicalUserData.organizationId || '';
          setViewMode((currentMode) => {
            const preferredMode = currentMode || savedViewMode;
            return resolveViewMode(canonicalUserData, orgId, preferredMode);
          });
          // Return the freshly resolved userData so callers (e.g. invite acceptance) can poll for the
          // canonical membership to become visible before navigating — without racing React state.
          return canonicalUserData;
        }
        console.warn('⚠️ No user profile found in Firestore.');
      } catch (err) {
        console.error('❌ Error refreshing user data:', err);
      }
    }
    return null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await refreshUserData();
      } else {
        setUserData(null);
        setViewMode('student');
        window.localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const updateViewMode = (nextMode) => {
    const orgId = userData?.orgId || userData?.organizationId || '';
    const resolvedMode = resolveViewMode(userData || {}, orgId, nextMode);
    setViewMode(resolvedMode);
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, resolvedMode);
  };

  // Pass 86P — switch the ACTIVE organization among the user's canonical memberships. Validates the target is a
  // real membership (selecting NEVER grants access), persists it to the ONE canonical preferred-org key, updates
  // the active orgId, and recomputes the allowed workspaces for the NEW org: keep the current workspace if it is
  // still valid there, otherwise move deterministically to the best valid one. Returns the resolved orgId.
  const setActiveOrganization = (orgId) => {
    const memberships = (userData && userData.organizationMemberships) || [];
    if (!orgId || !memberships.some((m) => m.organizationId === orgId)) return userData?.orgId || '';
    setPreferredOrganizationId(orgId);
    setUserData((prev) => (prev ? { ...prev, orgId } : prev));
    setViewMode((currentMode) => resolveViewMode({ ...(userData || {}), orgId }, orgId, currentMode));
    return orgId;
  };

  // Pass 58C — the workspaces this user may switch between (for the WorkspaceSwitcher). Serena (ops,
  // non-teaching) sees no Instructor option; a plain instructor sees no Operations option.
  const allowedViewModes = computeAllowedViewModes(userData || {}, userData?.orgId || userData?.organizationId || '');

  // ✅ DEV ONLY: Toggle between test accounts
  const loginAsTestUser = async (role) => {
    setLoading(true);
    try {
      const credentials = {
        instructor: {
          email: 'instructor@example.com',
          password: 'password123',
        },
        student: {
          email: 'student@example.com',
          password: 'password123',
        },
        organization: {
          email: 'org@example.com',
          password: 'password123',
        },
        admin: {
          email: 'admin@example.com',
          password: 'password123',
        },
      };
      const cred = credentials[role];
      if (!cred) { console.error(`Unknown role: ${role}`); return; }
      const { email, password } = cred;
      // Clear stale viewMode so the new account gets its correct default
      window.localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
      setViewMode(null);
      await signOut(auth);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error(`❌ Failed to login as ${role}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    userData,
    loading,
    viewMode,
    allowedViewModes,
    setViewMode: updateViewMode,
    setActiveOrganization,
    refreshUserData,
    loginAsTestUser,
    setUserData,
  };

  // Pass 86P — while canonical auth/identity/org data resolves, render a deterministic branded bootstrap shell
  // (never a blank screen, never a wrong/placeholder identity). The app transitions atomically to `children` once
  // `loading` is false. Context value is still provided during loading so nothing throws if read early.
  return (
    <AuthContext.Provider value={value}>
      {loading ? <BootstrapShell /> : children}
    </AuthContext.Provider>
  );
}
